/**
 * TODO:
 * Sanitize hash values
 * Open more tags if necessary when coming from /tagged/ pages
 * Make back button work
 * Add sort to URL hashes?
 * Remove "/tagged/tagname" from URL or redirect
 * Add page caching
 */

import initShopifyClient from '../initializers/init-shopify-client';

export class ArticleFilter {
	constructor(container, options = {}) {
		this.container = container;

		if (!container) {
			console.warn('No container found for ArticleFilter component');
			return;
		}

		if (!options.storeDomain || !options.publicAccessToken || !options.apiVersion) {
			console.warn('Missing required Shopify Frontend API options for ArticleFilter component: storeDomain, publicAccessToken, apiVersion');
			return;
		}

		const defaultOptions = {
			// classes
			hiddenClass: 'd-none',
			noResultsClass: 'no-results',
			gridItemClass: 'col',
			paginationButtonPrevClass: 'btn-pagination--prev',
			paginationButtonNextClass: 'btn-pagination--next',
			skeletonClass: 'skeleton-loader',

			// elements
			searchForm: '[data-search-form]',
			sortSelect: '[data-sort-select]',
			articleGrid: '[data-article-grid]',
			paginationWrapper: '[data-pagination]',
			filterGroup: '[data-filter-group]',

			// options
			graphQL: `
				title
				onlineStoreUrl
				image {
					src
				}`,
			scrollOnUpdate: true,
			scrollOffset: 84,

			// functions
			createCard: null,
		}

		this.options = { ...defaultOptions, ...options };

		if (!this.options.createCard || typeof this.options.createCard !== 'function') {
			console.warn('Missing createCard function for ArticleFilter component');
			return;
		}

		this.articleGrid = this.container.querySelector(this.options.articleGrid);
		this.noResults = this.container.querySelector('.' + this.options.noResultsClass);
		// this.loadedPages = []; // CACHE
		this.curPage = this.getPageFromURL()
		this.curCursor = null;
		this.filters = [];
		this.isInitialized = false;

		// Gathered from the JSON object in article-grid.liquid
		this.articlesPerPage =
			+window?.mx?.articleFilters?.articlesPerPage ?? 9999;
		this.blog = window?.mx?.articleFilters?.blog;
		this.fallbackImage = window?.mx?.articleFilters?.fallbackImage;
		this.moreTagsToggleAmount = +window?.mx?.articleFilters?.moreTagsToggleAmount ?? 999;

		// Bindings
		this.initFilters = this.initFilters.bind(this);
        this.initFilterChips = this.initFilterChips.bind(this);
		this.initPagination = this.initPagination.bind(this);
        this.initSearch = this.initSearch.bind(this);
        this.initSort = this.initSort.bind(this);
		this.initToggleTagsButtons = this.initToggleTagsButtons.bind(this);
        
		this.setURLHash = this.setURLHash.bind(this);
		this.getQuery = this.getQuery.bind(this);
		this.clearGrid = this.clearGrid.bind(this);
		this.update = this.update.bind(this);
		this.updatePagination = this.updatePagination.bind(this);
		this.getResults = this.getResults.bind(this);

		this.client = initShopifyClient(
            this.options.storeDomain,
            this.options.publicAccessToken,
            this.options.apiVersion,
        );
        if (!this.client) return;

        this.initSort();
		this.initFilters();
        this.initFilterChips();
		this.initPagination();
        this.initSearch();
		this.update(this.curPage);
	}

	setURLHash = () => {
		window.location.hash = this.filters
			.map((filter) => {
				return `${filter.name}=${filter.activeValues.join(',')}`;
			})
			.join('&')
			.concat(`&p=${this.curPage}`)
			.concat(this.searchInput ? `&q=${this.searchInput.value}` : '')
	};

	getPageFromURL = () => window.location.hash.split('p=').pop().split('&')[0] || 1

	getTaggedFromURL = () => {
		if (window.location.href.includes('/tagged/')) {
			return window.location.href.split('/tagged/')[1];
		}
		return false;
	};

	getQuery = () => {
		let query = `(blog_title:'${this.blog}')`;

        if (this.searchInput && this.searchInput.value != '') {
            return `${query} AND (${this.searchInput.value})`;
        }

		this.filters.forEach((filter) => {
			if (filter.activeValues.length) {
				query += ` AND (tag:${filter.activeValues.join(' OR tag:')})`;
			}
		});
		return query;
	};

	initFilters = () => {
		let that = this;
		const filterGroups = this.container.querySelectorAll(
			this.options.filterGroup,
		);
		filterGroups.forEach((filterGroup) => {
			const name = filterGroup.dataset.filterGroup;
			const filterButtons = Array.from(
				filterGroup.querySelectorAll('input[type="checkbox"]'),
			);
			const filterValues = filterButtons.map((button) => button.name);
			let activeValues = window.location.hash
				.split(name + '=')
				.pop()
				.split('&')[0]
				.split(',');

			if (activeValues.length === 1 && activeValues[0] === '') {
				activeValues = [];
			}

			filterButtons.forEach((button) => {
				if (activeValues.length > 0 && activeValues.length < filterValues.length - 1) {
					button.checked = activeValues.includes(button.name);
				} else if (button.dataset.filterBtnAll) {
					button.checked = true;
					activeValues = activeValues.length > 0 ? [] : activeValues;
				} else {
					button.checked = false;
				}
			});

			that.filters.push({
				name,
				filterButtons,
				filterValues,
				activeValues,
			});
		});

		const urlTag = this.getTaggedFromURL();
		if (urlTag) {
			this.filters.forEach((filter) => {
				if (filter.filterValues.includes(urlTag)) {
					filter.filterButtons.forEach((button) => {
						button.checked = button.name === urlTag;
					});
					filter.activeValues = [urlTag];
				}
			});
		}

		that.filters.forEach((filter) => {
			filter.filterButtons.forEach((button) => {
				button.addEventListener('change', () => {

					// Don't use search input since we're using filters
                    if (this.searchInput) {
                        this.searchInput.value = '';
                    }

					const allButton = filter.filterButtons.find(
						(button) => button.dataset.filterBtnAll === filter.name,
					);

					// If the "All" button is clicked, uncheck all other buttons
					if (button === allButton) {
						filter.filterButtons.forEach((button) => {
							if (button !== allButton) {
								button.checked = false;
							}
						});
						filter.activeValues = [];
					} else {
						filter.activeValues = filter.filterButtons
							.filter((button) => button.checked && button !== allButton)
							.map((button) => button.name);

						allButton.checked = filter.activeValues.length === 0;
					}

                    // Update filter chips TODO improve
					if (allButton.checked) {
						document.querySelectorAll('[data-filter-chip]').forEach(chip => {
							chip.classList.remove('active');
						})
					} else if (button.checked) {
                        document.querySelector('[data-filter-chip="' + button.name + '"]')?.classList.add('active');
                    } else {
                        document.querySelector('[data-filter-chip="' + button.name + '"]')?.classList.remove('active');
                    }

                    that.update(1, null, true);
				});
			});
		});

		filterGroups.forEach((filterGroup) => {
			that.initToggleTagsButtons(filterGroup);
		})
	};

    initFilterChips = () => {
        this.chips = this.container.querySelectorAll('[data-filter-chip]');
        this.chips.forEach((chip) => {
            const button = chip.querySelector('button');
            button?.addEventListener('click', (e) => {
                const filter = chip.dataset.filterChip
                document.querySelector('input[name="' + filter + '"]')?.click();
                chip.classList.remove('active');
            });
        })

        this.filters.forEach((filter) => {
            filter.activeValues.forEach((activeValue) => {
                document.querySelector('[data-filter-chip="' + activeValue + '"]')?.classList.add('active');
            })
        });

        const clearAllButton = this.container.querySelector('[data-filter-chip="all"]');
        clearAllButton?.addEventListener('click', (e) => {
            this.clearAll();
        })
    }

	initPagination = () => {
        this.pagination = this.container.querySelector(this.options.paginationWrapper);

		if (!this.pagination) {
			console.warn('No paginationWrapper found for ArticleFilter component');
			return;
		}

        const paginationPrev = this.pagination.querySelector('.' + this.options.paginationButtonPrevClass);
        const paginationNext = this.pagination.querySelector('.' + this.options.paginationButtonNextClass);

		if (!paginationPrev || !paginationNext) {
			console.warn('No pagination buttons found for ArticleFilter component');
			return;
		}

        paginationPrev.addEventListener('click', () => {
            this.update(+paginationPrev.dataset.page, paginationPrev.dataset.cursor !== '' ? paginationPrev.dataset.cursor : null);
        });
        paginationNext.addEventListener('click', () => {
            this.update(+paginationNext.dataset.page, paginationNext.dataset.cursor !== '' ? paginationNext.dataset.cursor : null);
        });
	};

    initSearch = () => {
		const searchForm = this.container.querySelector(this.options.searchForm);

		if (!searchForm) return;

        this.searchInput = searchForm.querySelector('input[type="search"], input[type="text"]');

        if (!this.searchInput) {
			console.warn('No search input found for ArticleFilter component inside: ' + searchForm);
			return;
		}

        searchForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.update(1, null, true);
        })

		const urlSearch = window.location.hash.split('q=').pop().split('&')[0] || '';
		if (urlSearch) {
			this.searchInput.value = urlSearch;
		}
    }

    initSort = () => {
        const sortSelects = this.container.querySelectorAll(this.options.sortSelect);

		// Default sort
        if (!sortSelects[0]) {
            this.sortKey = 'PUBLISHED_AT'
            this.sortReverse = false
            return;
        }

        this.updateSort(sortSelects[0].value);

		sortSelects.forEach((sortSelect) => {
			sortSelect.addEventListener('change', () => {
				sortSelects.forEach((select) => {
					if (select != sortSelect) {
						select.value = sortSelect.value;
					}
				})
				this.updateSort(sortSelect.value);
				this.update(1, null, true);
			});
		})
    }

	initToggleTagsButtons = (filterGroup) => {
		const toggleTagsButtons = filterGroup.querySelectorAll('[data-toggle-tags]');
		const tags = filterGroup.querySelectorAll('.tag');

		for (const toggleTagsButton of toggleTagsButtons) {
			toggleTagsButton.addEventListener('click', (e) => {
				e.preventDefault();
				for (const tag of tags) {
					tag.classList.toggle('active');
				}
			});
		}

		let thisGroup = this.filters.find((val) => {
			return val.name === filterGroup.dataset.filterGroup;
		})

		// Expand tags if a filter is selected beyond the more tags toggle amount
		for (const value of thisGroup.activeValues) {
			if (thisGroup.filterValues.indexOf(value) > this.moreTagsToggleAmount) {
				for (const tag of tags) {
					tag.classList.toggle('active');
				}
				break;
			}
		}
	}

    updateSort = (value) => {
		const splitValue = value.split('|');
		this.sortKey = splitValue[0];
		// If [1] exists, it's a reverse sort
		splitValue[1] ? this.sortReverse = true : this.sortReverse = false;
    }

	handleize = (str) => {
		return str
			.toLowerCase() // Convert to lowercase
			.replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric characters with hyphens
			.replace(/^-+|-+$/g, '') // Remove leading or trailing hyphens
			.replace(/-{2,}/g, '-'); // Replace multiple consecutive hyphens with a single hyphen
	}

    clearAll = () => {
        this.filters.forEach((filter) => {
            filter.filterButtons.forEach((button) => {
                if (button.dataset.filterBtnAll) {
                    button.checked = true;
                } else {
                    button.checked = false;
                }
            });
            filter.activeValues = [];
        });

        this.chips.forEach((chip) => {
            chip.classList.remove('active');
        });

        this.update(1, null, true);
    }

	clearGrid = () => {
		this.articleGrid
			.querySelectorAll('.' + this.options.gridItemClass)
			.forEach((gridItem) => {
				gridItem.remove();
			});
	};

	updatePagination = async (query, curPage) => {
		// Build pagination
		const totalArticlesQuery = `
		query TotalArticlesQuery($query: String, $cursor: String, $sortKey: ArticleSortKeys, $sortReverse: Boolean) {
			articles(first: 250, query: $query, after: $cursor, sortKey: $sortKey, reverse: $sortReverse) {
				edges {
					cursor
					node {
						title
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}`;

		const paginationArticles = []
		let hasNextPage = true;
		let paginationCursor = null;

		while(hasNextPage) {
			const { data, errors } =
				await this.client.request(totalArticlesQuery, {
					variables: {
						query,
						cursor: paginationCursor,
						sortKey: this.sortKey,
						sortReverse: this.sortReverse,
					},
				});

			if (errors) {
				console.error(
					'Errors loading total articles from the Storefront API: ',
					errors,
				);
				return false;
			}

			paginationArticles.push(...data.articles.edges);
			paginationCursor = data.articles.pageInfo.endCursor;
			hasNextPage = data.articles.pageInfo.hasNextPage;
		}

		const pages = Math.ceil(
			paginationArticles.length / this.articlesPerPage,
		);

		if (pages > 1) {
			const paginationLabel = this.pagination.querySelector('.pagination-label');
			const paginationPrev = this.pagination.querySelector('.' + this.options.paginationButtonPrevClass);
			const paginationNext = this.pagination.querySelector('.' + this.options.paginationButtonNextClass);

			paginationPrev.setAttribute('data-page', Math.max(+curPage - 1, 1));
			paginationNext.setAttribute('data-page', Math.min(+curPage + 1, pages));

			if (+curPage - 1 < 1) {
				paginationPrev.setAttribute('disabled', true);
			} else {
				paginationPrev.removeAttribute('disabled');
			}
			if (+curPage + 1 > pages) {
				paginationNext.setAttribute('disabled', true);
			} else {
				paginationNext.removeAttribute('disabled');
			}

			paginationPrev.setAttribute(
				'data-cursor',
				paginationArticles[(+curPage - 2) * this.articlesPerPage - 1]
					?.cursor ?? '',
			);
			paginationNext.setAttribute(
				'data-cursor',
				paginationArticles[+curPage * this.articlesPerPage - 1]
					?.cursor ?? '',
			)
			paginationLabel.innerHTML = `Page ${+curPage} of ${pages}`;

			this.pagination.classList.remove(this.options.hiddenClass);
		} else {
			this.pagination.classList.add(this.options.hiddenClass);
		}

		this.curCursor = paginationArticles[(+curPage - 1) * this.articlesPerPage - 1]?.cursor ?? null
		return this.curCursor
	}

	getResults = async (query, cursor) => {
		// If we've already loaded this page... // CACHE
		// if (this.loadedPages.includes(page)) {
		// 	this.articleGrid
		// 		.querySelectorAll('.' + this.options.gridItemClass)
		// 		.forEach((gridItem) => {
		// 			if (gridItem.dataset.page != page) {
		// 				gridItem.classList.add(this.options.hiddenClass);
		// 			} else {
		// 				gridItem.classList.remove(this.options.hiddenClass);
		// 			}
		// 		});

		// 	this.pagination.querySelectorAll('li').forEach((li) => {
		// 		const button = li.querySelector('button');
		// 		if (button.dataset.page == page) {
		// 			li.classList.add('active');
		// 		} else {
		// 			li.classList.remove('active');
		// 		}
		// 	});

		// 	return -1;
		// }

		// Load articles for this page
		const pagedArticlesQuery = `
            query pagedArticlesQuery($cursor: String, $num: Int, $query: String, $sortKey: ArticleSortKeys, $sortReverse: Boolean) {
                articles(first: $num, after: $cursor, query: $query, sortKey: $sortKey, reverse: $sortReverse) {
                    edges {
                        node {
                            ${this.options.graphQL}
                        }
                    }
                }
            }
        `;

		const { data, errors } = await this.client.request(pagedArticlesQuery, {
			variables: {
				cursor,
				num: this.articlesPerPage,
				query,
                sortKey: this.sortKey,
                sortReverse: this.sortReverse,
			},
		});

		if (errors) {
			console.error(
				'Errors loading articles from the Storefront API: ',
				errors,
			);
			return false;
		}

		// this.loadedPages.push(+page); // CACHE

		this.articleGrid
			.querySelectorAll('.' + this.options.gridItemClass)
			.forEach((gridItem) => {
				gridItem.remove();
				// if (gridItem.dataset.page !== page) {  // CACHE
				// 	gridItem.classList.add(this.options.hiddenClass);
				// } else {
				// 	 gridItem.classList.remove(this.options.hiddenClass);
				// }
			});

		return data.articles.edges.map((edge) => edge.node);
	}

	update = async (page = 1, cursor = null, clear = false) => {

		this.articleGrid
			.querySelectorAll('.' + this.options.gridItemClass)
			.forEach((gridItem) => {
				gridItem.classList.add(this.options.skeletonClass);
			});

		const query = this.getQuery();
		this.curPage = page;

		if (this.isInitialized && this.options.scrollOnUpdate) {
			window.scrollTo({
				top: this.container.offsetTop - this.options.scrollOffset,
				behavior: 'smooth',
			})
		} else {
			this.isInitialized = true;
		}

		this.setURLHash();

		let articles = [];

		cursor = await this.updatePagination(query, page);
        articles = await this.getResults(query, cursor);

		if (clear && articles != -1) {
			this.clearGrid();
		}

		if (articles === -1 || articles.length > 0) {
			this.noResults.classList.add(this.options.hiddenClass);
		} else {
			this.noResults.classList.remove(this.options.hiddenClass);
		}

		if (articles.length > 0) {
			articles.forEach((article, i) => {
				const card = this.options.createCard({
					filter: this,
					article,
					page,
					order: (page - 1) * (this.articlesPerPage || 0) + i,
					blog: this.blog
				});
				this.articleGrid.appendChild(card);
			});
		}
	};
}
